import { checkPropertyAccess } from '@/lib/company-access'
import type { RoomAsset } from '@/models'
import { neon } from '@/services/neon'

async function checkAssetAccess(assetId: string, action: string): Promise<void> {
  const { data: asset } = await neon
    .from('room_assets')
    .select('room_id')
    .eq('id', assetId)
    .single()
  if (!asset) throw new Error('Asset not found.')

  const { data: room } = await neon
    .from('rooms')
    .select('property_id')
    .eq('id', (asset as { room_id: string }).room_id)
    .single()
  const propertyId = (room as { property_id: string } | null)?.property_id
  if (!propertyId) throw new Error('Room not found.')

  await checkPropertyAccess(propertyId, action)
}

async function checkRoomAssetAccess(roomId: string, action: string): Promise<void> {
  const { data: room } = await neon.from('rooms').select('property_id').eq('id', roomId).single()
  const propertyId = (room as { property_id: string } | null)?.property_id
  if (!propertyId) throw new Error('Room not found.')

  await checkPropertyAccess(propertyId, action)
}

export async function fetchRoomAssets(roomId: string): Promise<RoomAsset[]> {
  await checkRoomAssetAccess(roomId, 'view')

  const { data, error } = await neon
    .from('room_assets')
    .select('*')
    .eq('room_id', roomId)
    .order('created_at', { ascending: true })

  if (error) throw new Error(error.message)
  return (data as RoomAsset[]) || []
}

export async function createRoomAsset(payload: Partial<RoomAsset>): Promise<RoomAsset> {
  if (!payload.room_id) throw new Error('Room ID is required.')
  await checkRoomAssetAccess(payload.room_id, 'create')

  const { data, error } = await neon.from('room_assets').insert(payload).select().single()
  if (error) throw new Error(error.message)
  return data as RoomAsset
}

export async function updateRoomAsset(id: string, payload: Partial<RoomAsset>): Promise<RoomAsset> {
  await checkAssetAccess(id, 'update')

  const { data, error } = await neon
    .from('room_assets')
    .update(payload)
    .eq('id', id)
    .select()
    .single()

  if (error) throw new Error(error.message)
  return data as RoomAsset
}

export async function deleteRoomAsset(id: string): Promise<void> {
  await checkAssetAccess(id, 'delete')

  const { error } = await neon.from('room_assets').delete().eq('id', id)
  if (error) throw new Error(error.message)
}
